import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { ApiError } from '../../api/httpClient'
import { getSongToneSignature, searchSongs } from '../../api/songKnowledgeApi'
import { getVideoPreview } from '../../api/videoPreviewApi'
import type { DemoLanguage } from '../../models/DemoAnalysis'
import type { DemoSourceSummary, DemoToneAnalysisErrors } from '../../models/demoToneAnalysis'
import type { SongSearchResult, ToneSignatureResultDto, ToneSignatureUiModel } from '../../models/toneSignature'
import type { VideoPreview, VideoPreviewStatus } from '../../models/videoPreview'
import { getDemoSourceSummary } from '../../utils/buildToneAnalysisRequest'
import { toToneSignatureUiModel } from '../../utils/toneSignatureMapper'
import { derivePresetBaseName, parseYouTubePreviewMetadata } from '../../utils/youtubePreviewMetadata'
import { AnalysisWorkflow, type AnalysisWorkflowStepId } from './AnalysisWorkflow'
import { ToneAnalysisForm, type ToneAnalysisFormValues } from './ToneAnalysisForm'
import { ToneSignaturePage } from './ToneSignaturePage'

type InteractiveDemoProps = {
  language: DemoLanguage
}

type DemoStatus = 'idle' | 'discovering' | 'signature' | 'error'
type MetadataField = 'songTitle' | 'artist' | 'presetBaseName'
type ScrollTarget = 'beta' | 'demo'
type DemoErrorKind = 'song-unavailable' | 'signature-unavailable' | 'load-failed'

class DemoFlowError extends Error {
  readonly kind: DemoErrorKind

  constructor(kind: DemoErrorKind, message: string) {
    super(message)
    this.name = 'DemoFlowError'
    this.kind = kind
  }
}

const discoveryStepIds: AnalysisWorkflowStepId[] = [
  'metadata',
  'knowledge',
  'signature',
  'ready',
]

const discoveryLabels: Record<DemoLanguage, Record<AnalysisWorkflowStepId, string>> = {
  en: {
    metadata: 'Extracting video metadata',
    knowledge: 'Searching Knowledge Database',
    signature: 'Loading Tone Signature',
    ready: 'Tone Signature ready',
  },
  it: {
    metadata: 'Estrazione metadati video',
    knowledge: 'Ricerca nel Knowledge Database',
    signature: 'Caricamento Tone Signature',
    ready: 'Tone Signature pronta',
  },
}

const transparencyCopy: Record<DemoLanguage, string> = {
  en: 'Knowledge-backed result - loaded from the ToneSignature Knowledge Database.',
  it: 'Risultato basato sulla Knowledge - caricato dal Knowledge Database ToneSignature.',
}

const visualStepDelayMs = import.meta.env.MODE === 'test' ? 70 : 900
const reducedMotionStepDelayMs = import.meta.env.MODE === 'test' ? 40 : 220
const completionDelayMs = import.meta.env.MODE === 'test' ? 80 : 650
const reducedMotionCompletionDelayMs = import.meta.env.MODE === 'test' ? 80 : 220
const previewDebounceMs = import.meta.env.MODE === 'test' ? 10 : 350

const initialValues: ToneAnalysisFormValues = {
  songTitle: '',
  artist: '',
  presetBaseName: '',
  maximumGuitarLayers: 2,
  sourceMode: 'youtube',
  youtubeUrl: '',
  libraryArtist: '',
  libraryTitle: '',
}

function sleep(durationMs: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }

    const timeoutId = window.setTimeout(resolve, durationMs)
    signal.addEventListener('abort', () => {
      window.clearTimeout(timeoutId)
      reject(new DOMException('Aborted', 'AbortError'))
    }, { once: true })
  })
}

function getErrorMessage(error: unknown) {
  if (error instanceof DemoFlowError) {
    return error.message
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return ''
  }

  if (error instanceof ApiError) {
    return error.message
  }

  return 'Unable to load Tone Signature.'
}

function getSongSearchTerms(values: ToneAnalysisFormValues) {
  return values.sourceMode === 'library'
    ? {
        artist: values.libraryArtist.trim(),
        title: values.libraryTitle.trim(),
      }
    : {
        artist: values.artist.trim(),
        title: values.songTitle.trim(),
      }
}

function logDevelopment(label: string, value: unknown) {
  if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
    console.log(label, value)
  }
}

function getPreviewErrorMessage(error: unknown) {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return ''
  }

  if (error instanceof ApiError) {
    return error.message
  }

  return 'YouTube preview could not be loaded.'
}

function isValidYouTubePreviewUrl(value: string) {
  try {
    const url = new URL(value.trim())
    const host = url.hostname.toLowerCase()

    return host === 'youtube.com' || host.endsWith('.youtube.com') || host === 'youtu.be'
  } catch {
    return false
  }
}

function getStickyHeaderHeight() {
  return document.querySelector<HTMLElement>('.site-header')?.getBoundingClientRect().height ?? 0
}

function getTargetScrollY(element: HTMLElement, headerHeight: number) {
  return element.getBoundingClientRect().top + window.scrollY - headerHeight - 16
}

function isBetaFormVisible(headerHeight: number) {
  const beta = document.getElementById('beta')
  const form = beta?.querySelector<HTMLElement>('.beta-form')
  const emailInput = beta?.querySelector<HTMLInputElement>('input[name="email"]')
  const visibilityTarget = form ?? beta

  if (!beta || !visibilityTarget) {
    return false
  }

  const visibleViewportHeight = window.innerHeight - headerHeight
  const targetRect = visibilityTarget.getBoundingClientRect()

  if (targetRect.height <= visibleViewportHeight) {
    return targetRect.top >= headerHeight && targetRect.bottom <= window.innerHeight
  }

  const emailRect = emailInput?.getBoundingClientRect()
  return beta.getBoundingClientRect().top <= headerHeight + 1
    && (!emailRect || (emailRect.top >= headerHeight && emailRect.bottom <= window.innerHeight))
}

export function InteractiveDemo({ language }: InteractiveDemoProps) {
  const [values, setValues] = useState<ToneAnalysisFormValues>(initialValues)
  const [errors, setErrors] = useState<DemoToneAnalysisErrors>({})
  const [status, setStatus] = useState<DemoStatus>('idle')
  const [activeStepIndex, setActiveStepIndex] = useState(0)
  const [failedStepIndex, setFailedStepIndex] = useState<number | null>(null)
  const [selectedToneSignature, setSelectedToneSignature] = useState<ToneSignatureUiModel | null>(null)
  const [sourceSummary, setSourceSummary] = useState<DemoSourceSummary | null>(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [videoPreview, setVideoPreview] = useState<VideoPreview | null>(null)
  const [videoPreviewStatus, setVideoPreviewStatus] = useState<VideoPreviewStatus>('idle')
  const [videoPreviewError, setVideoPreviewError] = useState('')
  const [videoPreviewRetryKey, setVideoPreviewRetryKey] = useState(0)
  const [pendingScrollTarget, setPendingScrollTarget] = useState<ScrollTarget | null>(null)
  const shouldFocusBetaEmail = useRef(false)
  const abortController = useRef<AbortController | null>(null)
  const previewAbortController = useRef<AbortController | null>(null)
  const runId = useRef(0)
  const previewRunId = useRef(0)
  const activeStepIndexRef = useRef(0)
  const lastAutoFilledPreviewUrl = useRef<string | null>(null)
  const metadataFieldEdited = useRef<Record<MetadataField, boolean>>({
    songTitle: false,
    artist: false,
    presetBaseName: false,
  })
  const reduce = useReducedMotion()

  useEffect(() => {
    return () => {
      runId.current += 1
      previewRunId.current += 1
      abortController.current?.abort()
      previewAbortController.current?.abort()
    }
  }, [])

  useEffect(() => {
    if (!pendingScrollTarget) {
      return
    }

    let firstFrameId = 0
    let secondFrameId = 0
    let visibilityCheckId = 0
    let scrollEndHandled = false

    const verifyBetaVisibility = () => {
      if (pendingScrollTarget !== 'beta' || scrollEndHandled) {
        return
      }

      scrollEndHandled = true
      window.removeEventListener('scrollend', verifyBetaVisibility)

      const beta = document.getElementById('beta')
      if (!beta) {
        return
      }

      const headerHeight = getStickyHeaderHeight()
      if (!isBetaFormVisible(headerHeight)) {
        window.scrollTo({
          top: getTargetScrollY(beta, headerHeight),
          behavior: 'auto',
        })
      }

      setPendingScrollTarget(null)
    }

    firstFrameId = window.requestAnimationFrame(() => {
      secondFrameId = window.requestAnimationFrame(() => {
        const target = document.getElementById(pendingScrollTarget)

        if (!target) {
          setPendingScrollTarget(null)
          return
        }

        const headerHeight = getStickyHeaderHeight()
        window.scrollTo({
          top: getTargetScrollY(target, headerHeight),
          behavior: 'smooth',
        })

        if (pendingScrollTarget === 'beta') {
          window.addEventListener('scrollend', verifyBetaVisibility, { once: true })
          visibilityCheckId = window.setTimeout(verifyBetaVisibility, 700)

          if (shouldFocusBetaEmail.current) {
            const emailInput = document.querySelector<HTMLInputElement>('#beta input[name="email"]')
            emailInput?.focus({ preventScroll: true })
            shouldFocusBetaEmail.current = false
          }
        }

        if (pendingScrollTarget !== 'beta') {
          setPendingScrollTarget(null)
        }
      })
    })

    return () => {
      window.cancelAnimationFrame(firstFrameId)
      window.cancelAnimationFrame(secondFrameId)
      window.clearTimeout(visibilityCheckId)
      window.removeEventListener('scrollend', verifyBetaVisibility)
    }
  }, [pendingScrollTarget, status, selectedToneSignature])

  function requestScrollToTarget(target: ScrollTarget, focusEmail = false) {
    shouldFocusBetaEmail.current = target === 'beta' && focusEmail
    setPendingScrollTarget(target)
  }

  function closeAnalysisResult() {
    setStatus('idle')
    setActiveStepIndex(0)
    setFailedStepIndex(null)
    setSelectedToneSignature(null)
    setErrorMessage('')
  }

  function applyPreviewMetadata(preview: VideoPreview, url: string) {
    const isDifferentLoadedUrl = lastAutoFilledPreviewUrl.current !== null && lastAutoFilledPreviewUrl.current !== url
    if (isDifferentLoadedUrl) {
      metadataFieldEdited.current = {
        songTitle: false,
        artist: false,
        presetBaseName: false,
      }
    }

    lastAutoFilledPreviewUrl.current = url
    const metadata = parseYouTubePreviewMetadata(preview)

    setValues((current) => ({
      ...current,
      songTitle: metadataFieldEdited.current.songTitle ? current.songTitle : metadata.songTitle,
      artist: metadataFieldEdited.current.artist ? current.artist : metadata.artist,
      presetBaseName: metadataFieldEdited.current.presetBaseName ? current.presetBaseName : metadata.presetBaseName,
    }))
  }

  function resetAnalysisFlow() {
    runId.current += 1
    previewRunId.current += 1
    abortController.current?.abort()
    previewAbortController.current?.abort()
    activeStepIndexRef.current = 0
    lastAutoFilledPreviewUrl.current = null
    metadataFieldEdited.current = {
      songTitle: false,
      artist: false,
      presetBaseName: false,
    }
    setValues(initialValues)
    setErrors({})
    setStatus('idle')
    setActiveStepIndex(0)
    setFailedStepIndex(null)
    setSelectedToneSignature(null)
    setSourceSummary(null)
    setErrorMessage('')
    setVideoPreview(null)
    setVideoPreviewStatus('idle')
    setVideoPreviewError('')
    setVideoPreviewRetryKey(0)
  }

  useEffect(() => {
    previewRunId.current += 1
    const currentPreviewRunId = previewRunId.current
    previewAbortController.current?.abort()

    if (values.sourceMode !== 'youtube') {
      return
    }

    const url = values.youtubeUrl.trim()

    if (!url || !isValidYouTubePreviewUrl(url)) {
      return
    }

    const nextPreviewAbortController = new AbortController()
    previewAbortController.current = nextPreviewAbortController

    const timeoutId = window.setTimeout(async () => {
      try {
        const preview = await getVideoPreview(url, nextPreviewAbortController.signal)
        if (previewRunId.current !== currentPreviewRunId) {
          return
        }

        applyPreviewMetadata(preview, url)
        setVideoPreview(preview)
        setVideoPreviewStatus('loaded')
      } catch (error) {
        if (previewRunId.current !== currentPreviewRunId) {
          return
        }

        const message = getPreviewErrorMessage(error)
        if (!message) {
          return
        }

        setVideoPreviewError(message)
        setVideoPreviewStatus('error')
      }
    }, previewDebounceMs)

    return () => {
      window.clearTimeout(timeoutId)
      nextPreviewAbortController.abort()
    }
  }, [values.sourceMode, values.youtubeUrl, videoPreviewRetryKey])

  function validateDiscovery() {
    const nextErrors: DemoToneAnalysisErrors = {}

    if (values.maximumGuitarLayers !== 1 && values.maximumGuitarLayers !== 2) {
      nextErrors.maximumGuitarLayers = 'Choose rhythm only or rhythm plus lead.'
    }

    if (values.sourceMode === 'youtube') {
      if (!values.youtubeUrl.trim()) {
        nextErrors.youtubeUrl = 'Enter a YouTube URL for the demo.'
      } else if (!isValidYouTubePreviewUrl(values.youtubeUrl)) {
        nextErrors.youtubeUrl = 'Enter a valid YouTube URL for the demo.'
      } else if (videoPreviewStatus !== 'loaded' || !videoPreview) {
        nextErrors.youtubeUrl = 'Load the YouTube preview before analysis.'
      }
    }

    if (values.sourceMode === 'library') {
      if (!values.libraryArtist.trim() && !values.libraryTitle.trim()) {
        nextErrors.librarySearch = 'Enter an artist or title to search the Tone Library.'
      }
    }

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  function clearSourceErrors() {
    setErrors((current) => {
      const remaining = { ...current }
      delete remaining.youtubeUrl
      delete remaining.librarySearch
      return remaining
    })
  }

  function updateActiveStepIndex(index: number) {
    activeStepIndexRef.current = index
    setActiveStepIndex(index)
  }

  async function resolveSongFromKnowledgeDatabase(signal: AbortSignal): Promise<{
    selected: SongSearchResult
    toneSignature: ToneSignatureUiModel
  }> {
    const terms = getSongSearchTerms(values)
    let matches: SongSearchResult[]

    try {
      matches = await searchSongs(terms.artist, terms.title, signal)
      logDevelopment('Song search response', matches)
    } catch {
      throw new DemoFlowError('load-failed', 'Unable to load Tone Signature.')
    }

    const selected = matches[0]
    if (!selected) {
      throw new DemoFlowError('song-unavailable', 'This song is not available in the ToneSignature library yet.')
    }

    let toneSignatureResponse: ToneSignatureResultDto
    try {
      toneSignatureResponse = await getSongToneSignature(selected.songId, signal)
      logDevelopment('Tone Signature API response', toneSignatureResponse)
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        throw new DemoFlowError('signature-unavailable', 'Tone Signature not available.')
      }

      throw new DemoFlowError('load-failed', 'Unable to load Tone Signature.')
    }

    const toneSignature = toToneSignatureUiModel(toneSignatureResponse)
    logDevelopment('Tone Signature UI model', toneSignature)

    return { selected, toneSignature }
  }

  async function runDiscoveryWorkflow(signal: AbortSignal, currentRunId: number) {
    const delay = reduce ? reducedMotionStepDelayMs : visualStepDelayMs

    for (let index = 0; index < discoveryStepIds.length - 1; index += 1) {
      if (runId.current !== currentRunId) {
        return
      }

      updateActiveStepIndex(index)
      await sleep(delay, signal)
    }

    updateActiveStepIndex(discoveryStepIds.length - 2)
  }

  async function handleAnalyze() {
    if (status === 'discovering' || !validateDiscovery()) {
      return
    }

    abortController.current?.abort()
    const nextAbortController = new AbortController()
    const currentRunId = runId.current + 1
    runId.current = currentRunId
    abortController.current = nextAbortController
    setStatus('discovering')
    updateActiveStepIndex(0)
    setFailedStepIndex(null)
    setErrorMessage('')
    setSelectedToneSignature(null)
    setSourceSummary(getDemoSourceSummary(values))

    const requestPromise = resolveSongFromKnowledgeDatabase(nextAbortController.signal)
    const visualPromise = runDiscoveryWorkflow(nextAbortController.signal, currentRunId)

    try {
      const [response] = await Promise.all([requestPromise, visualPromise])
      if (runId.current !== currentRunId) {
        return
      }

      updateActiveStepIndex(discoveryStepIds.length - 1)
      await sleep(reduce ? reducedMotionCompletionDelayMs : completionDelayMs, nextAbortController.signal)

      if (runId.current !== currentRunId) {
        return
      }

      setSelectedToneSignature(response.toneSignature)
      setSourceSummary(values.sourceMode === 'library'
        ? { type: 'Tone Library', name: `${response.selected.artist} - ${response.selected.title}` }
        : getDemoSourceSummary(values))
      setValues((current) => ({
        ...current,
        songTitle: response.selected.title,
        artist: response.selected.artist,
        presetBaseName: derivePresetBaseName(response.selected.title),
        libraryArtist: current.sourceMode === 'library' ? response.selected.artist : current.libraryArtist,
        libraryTitle: current.sourceMode === 'library' ? response.selected.title : current.libraryTitle,
      }))
      updateActiveStepIndex(discoveryStepIds.length)
      setStatus('signature')
    } catch (error) {
      nextAbortController.abort()

      if (runId.current !== currentRunId) {
        return
      }

      const message = getErrorMessage(error)
      if (!message) {
        return
      }

      setFailedStepIndex((current) => current ?? activeStepIndexRef.current)
      setErrorMessage(message)
      setStatus('error')
    }
  }

  function handleAnalyzeAnotherSong() {
    resetAnalysisFlow()
    requestScrollToTarget('demo')
  }

  function handleGeneratePresetClick() {
    closeAnalysisResult()
    requestScrollToTarget('beta', true)
  }

  function handleJoinBeta() {
    closeAnalysisResult()
    requestScrollToTarget('beta', true)
  }

  function resetPreviewForValues(nextValues: ToneAnalysisFormValues) {
    previewRunId.current += 1
    previewAbortController.current?.abort()
    setVideoPreview(null)
    setVideoPreviewError('')
    setVideoPreviewStatus(
      nextValues.sourceMode === 'youtube' && isValidYouTubePreviewUrl(nextValues.youtubeUrl)
        ? 'loading'
        : 'idle',
    )
  }

  function handleFormChange(nextValues: ToneAnalysisFormValues) {
    if (nextValues.sourceMode !== values.sourceMode || nextValues.youtubeUrl !== values.youtubeUrl) {
      resetPreviewForValues(nextValues)
    }

    if (status === 'idle' || status === 'error') {
      setSelectedToneSignature(null)
      setSourceSummary(null)
      setErrorMessage('')
      setFailedStepIndex(null)
      updateActiveStepIndex(0)
    }

    setValues(nextValues)
  }

  function handleMetadataFieldEdit(field: MetadataField) {
    metadataFieldEdited.current[field] = true
  }

  function handleRetryPreview() {
    resetPreviewForValues(values)
    setVideoPreviewRetryKey((current) => current + 1)
  }

  const animationProps = reduce
    ? {}
    : { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -10 }, transition: { duration: 0.28 } }
  const discoverySteps = discoveryStepIds.map((id, index) => ({
    id,
    label: discoveryLabels[language][id],
    state: failedStepIndex === index
      ? 'failed' as const
      : index < activeStepIndex
        ? 'completed' as const
        : index === activeStepIndex
          ? 'active' as const
          : 'pending' as const,
  }))
  const hasPreviewableYouTubeUrl = values.sourceMode === 'youtube' && isValidYouTubePreviewUrl(values.youtubeUrl)
  const canAnalyze = values.sourceMode === 'library' || !hasPreviewableYouTubeUrl || videoPreviewStatus === 'loaded'

  return <div className="ts-demo-shell">
    <AnimatePresence mode="wait">
      {(status === 'idle' || status === 'discovering' || status === 'error') && <motion.div key="analysis-form" className="ts-demo-input" {...animationProps}>
        <ToneAnalysisForm
          values={values}
          errors={errors}
          preview={videoPreview}
          previewStatus={videoPreviewStatus}
          previewError={videoPreviewError}
          isLoading={status === 'discovering'}
          canAnalyze={canAnalyze}
          onChange={handleFormChange}
          onClearSourceErrors={clearSourceErrors}
          onMetadataFieldEdit={handleMetadataFieldEdit}
          onRetryPreview={handleRetryPreview}
          onSubmit={handleAnalyze}
        />
        {(status === 'discovering' || status === 'error') && <AnalysisWorkflow
          steps={discoverySteps}
          transparency={transparencyCopy[language]}
        />}
        {status === 'error' && <div className="ts-analysis-error" role="alert">
          {errorMessage}
        </div>}
        <small className="ts-analysis-transparency">
          {transparencyCopy[language]}
        </small>
      </motion.div>}

      {status === 'signature' && selectedToneSignature && <motion.div key={selectedToneSignature.songId} className="ts-demo-result api-backed" {...animationProps}>
        <ToneSignaturePage
          toneSignature={selectedToneSignature}
          sourceSummary={sourceSummary}
          onAnalyzeAnother={handleAnalyzeAnotherSong}
          onGeneratePreset={handleGeneratePresetClick}
        />
      </motion.div>}
    </AnimatePresence>
  </div>
}
